import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {ReposListResults} from '@github-ui/repos-list'
import {getScreenReaderMessage, sortingOptionsWithRelevance} from '@github-ui/repos-list'
import {useEffect} from 'react'

const debounceAnnounce = debounce(announce, 300)

function getEmptyMessage({
  filtered,
  currentPage,
  pageCount,
  errorMessage,
}: {
  filtered: boolean
  currentPage: number
  pageCount: number
  errorMessage?: string
}): {heading: string; description?: string} {
  if (errorMessage) {
    return {heading: 'Invalid query', description: errorMessage}
  }
  if (pageCount > 0 && currentPage > pageCount) {
    return {heading: 'No more repositories.', description: 'Visit a lower page.'}
  }
  if (filtered) {
    return {heading: 'No repositories matched your search.', description: 'Try a different search query.'}
  }
  return {heading: 'Enter a query to start searching.'}
}

/**
 * Hook to manage screen reader announcements for the repos finder page.
 * Announces search results and empty states to screen reader users.
 * @param isFetching - Whether a fetch is currently in progress.
 * @param currentPage - The current page number.
 * @param fetchedQuery - The query string that was fetched.
 * @param results - The current list results.
 * @param defaultSortingKey - The default sorting key to use.
 * @param errorMessage - Optional error message to announce when the query is invalid or results in an error.
 * @returns An object containing the empty messages for display.
 */
export function useFinderScreenReaderAnnouncements(
  isFetching: boolean,
  currentPage: number,
  fetchedQuery: string,
  results: ReposListResults,
  defaultSortingKey: string,
  errorMessage?: string,
) {
  const emptyMessages = getEmptyMessage({
    filtered: !!fetchedQuery,
    currentPage,
    pageCount: results.pageCount,
    errorMessage,
  })
  const message = getScreenReaderMessage(
    sortingOptionsWithRelevance,
    defaultSortingKey,
    () => emptyMessages,
    isFetching,
    currentPage,
    fetchedQuery,
    results,
  )

  useEffect(() => {
    if (message) debounceAnnounce(message)
  }, [message])

  return {emptyMessages}
}
